import { useState, useEffect } from 'react';
import { referralAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, Copy, Check, Share2, Users, Gift, Coins, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';

export default function ReferralPage() {
  const [link, setLink] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const [linkRes, statsRes] = await Promise.all([referralAPI.getLink(), referralAPI.getStats()]);
      setLink(linkRes.data);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const referralUrl = link ? `${window.location.origin}/?ref=${link.code}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <AppShell><div className="flex items-center justify-center h-[calc(100vh-3.5rem)]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--brand))]" /></div></AppShell>;

  return (
    <AppShell>
      <div className="p-6 md:p-8 max-w-4xl animate-fade-in" data-testid="referral-page">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'DM Sans' }}>Refer & Earn</h1>
          <p className="text-muted-foreground mt-1 text-sm">Share your referral link and earn credits when others sign up</p>
        </div>

        {/* Reward Info Boxes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-testid="referral-rewards-section">
          {/* New User Reward */}
          <div className="rounded-xl border-2 border-[hsl(var(--brand))]/20 bg-[hsl(var(--brand))]/5 p-6 text-center" data-testid="new-user-reward-box">
            <div className="w-14 h-14 rounded-full bg-[hsl(var(--brand))]/10 flex items-center justify-center mx-auto mb-3">
              <UserPlus className="w-7 h-7 text-[hsl(var(--brand))]" />
            </div>
            <h3 className="font-bold text-lg mb-1" style={{ fontFamily: 'DM Sans' }}>For Your Friend</h3>
            <div className="flex items-center justify-center gap-1.5 mb-2">
              <Coins className="w-5 h-5 text-[hsl(var(--brand))]" />
              <span className="text-3xl font-bold font-mono text-[hsl(var(--brand))]">5</span>
              <span className="text-sm text-muted-foreground">free credits</span>
            </div>
            <p className="text-xs text-muted-foreground">When they sign up for a paid plan using your link</p>
          </div>

          {/* Referrer Reward */}
          <div className="rounded-xl border-2 border-green-500/20 bg-green-500/5 p-6 text-center" data-testid="referrer-reward-box">
            <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-3">
              <Gift className="w-7 h-7 text-green-600" />
            </div>
            <h3 className="font-bold text-lg mb-1" style={{ fontFamily: 'DM Sans' }}>For You</h3>
            <div className="flex items-center justify-center gap-1.5 mb-2">
              <Coins className="w-5 h-5 text-green-600" />
              <span className="text-3xl font-bold font-mono text-green-600">30</span>
              <span className="text-sm text-muted-foreground">credits added</span>
            </div>
            <p className="text-xs text-muted-foreground">Added to your subscription when they subscribe</p>
          </div>
        </div>

        {/* Referral Link Card */}
        <div className="rounded-lg border bg-card p-6 mb-8" data-testid="referral-link-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--brand))]/10 flex items-center justify-center">
              <Share2 className="w-5 h-5 text-[hsl(var(--brand))]" />
            </div>
            <div>
              <h2 className="font-semibold">Your Referral Link</h2>
              <p className="text-xs text-muted-foreground">Share this link to invite new organizations</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Input value={referralUrl} readOnly className="font-mono text-sm bg-secondary/50" data-testid="referral-url-input" />
            <Button onClick={copyLink} variant="outline" className="shrink-0 gap-1.5" data-testid="copy-referral-link">
              {copied ? <><Check className="w-4 h-4 text-green-500" />Copied</> : <><Copy className="w-4 h-4" />Copy</>}
            </Button>
          </div>
          {link && <p className="text-xs text-muted-foreground mt-2 font-mono">Code: {link.code}</p>}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="rounded-lg border bg-card p-5" data-testid="referral-total-count">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand))]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[hsl(var(--brand))]" />
              </div>
              <div>
                <p className="text-3xl font-bold font-mono">{stats?.total_referrals || 0}</p>
                <p className="text-xs text-muted-foreground">Total Signups</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-card p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Coins className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-3xl font-bold font-mono text-green-600">{(stats?.total_referrals || 0) * 30}</p>
                <p className="text-xs text-muted-foreground">Credits Earned</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-card p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand))]/10 flex items-center justify-center">
                <Gift className="w-5 h-5 text-[hsl(var(--brand))]" />
              </div>
              <div>
                <p className="text-3xl font-bold font-mono">{(stats?.total_referrals || 0) * 5}</p>
                <p className="text-xs text-muted-foreground">Credits Given Away</p>
              </div>
            </div>
          </div>
        </div>

        {/* Signup History */}
        {stats?.signups?.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">Signup History</h3>
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Date</TableHead><TableHead>Your Reward</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {stats.signups.map(s => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">{s.new_user_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{s.new_user_email}</TableCell>
                      <TableCell className="text-xs text-muted-foreground font-mono">{new Date(s.created_at).toLocaleDateString()}</TableCell>
                      <TableCell><Badge className="text-xs bg-green-500/10 text-green-600 border-green-500/20 gap-1"><Coins className="w-3 h-3" />+30 credits</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
